/* =========================================================
   bmyjoy ASCII split-flap hero — animation engine
   =========================================================
   Reads character config from a JSON <script> block injected
   by the Liquid template. Fetches ASCII text files, then
   cycles between them with a solari-style mechanical flip.

   Characters are dynamic — managed as blocks in the customizer.
   Supports 1–5 characters, skipping those without content for
   the active view.

   Includes an RGB-split glitch effect that triggers periodically
   based on the glitch_intensity slider.
   ========================================================= */

(function () {
  'use strict';

  const RAMP = '@#8&$%*+=-:. ';
  const FLIP_DURATION = 1500;
  const WAVE_SPREAD = 400;

  const hero = document.querySelector('[data-bmyjoy-hero]');
  if (!hero) return;

  const asciiEl = hero.querySelector('[data-bmyjoy-hero-ascii]');
  if (!asciiEl) return;

  // ── Read config from JSON script block ─────────────────────

  const configEl = hero.querySelector('script[data-bmyjoy-hero-config]');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn('bmyjoy hero: failed to parse config', e);
    return;
  }

  const characters = config.characters || [];
  if (!characters.length) return;

  const cycleMs = parseInt(hero.dataset.cycleMs, 10) || 3000;
  const defaultView = hero.dataset.defaultView || 'ascii';
  const glitchIntensity = parseFloat(getComputedStyle(hero).getPropertyValue('--bmyjoy-hero-glitch')) || 0;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // ── State ──────────────────────────────────────────────────

  let portraits = [];       // array of string arrays (lines per character, null if no ASCII)
  let gridRows = 0;
  let gridCols = 0;
  let currentIndex = 0;
  let cycleTimer = null;
  let isFlipping = false;
  let isVisible = true;
  let activeView = defaultView;

  // ── DOM references ─────────────────────────────────────────

  const asciiLayer = hero.querySelector('[data-bmyjoy-layer="ascii"]');
  const productLayer = hero.querySelector('[data-bmyjoy-layer="product"]');
  const toggleWrap = hero.querySelector('[data-bmyjoy-hero-toggle]');
  const ctaEl = hero.querySelector('.bmyjoy-hero__cta');
  const ctaDefaultHref = ctaEl ? ctaEl.getAttribute('href') : '';

  // Product images indexed by character position
  const productImgMap = {};
  hero.querySelectorAll('.bmyjoy-hero__product-img').forEach(function (img) {
    var idx = parseInt(img.dataset.charIndex, 10);
    productImgMap[idx] = img;
  });

  // ── Which characters are valid for each view ───────────────

  var asciiIndices = [];
  var productIndices = [];
  characters.forEach(function (c, i) {
    if (c.asciiUrl) asciiIndices.push(i);
    if (c.hasProduct) productIndices.push(i);
  });

  function nextIndexForView(current, view) {
    var pool = (view === 'product') ? productIndices : asciiIndices;
    if (!pool.length) return current;
    var pos = pool.indexOf(current);
    if (pos === -1) return pool[0];
    return pool[(pos + 1) % pool.length];
  }

  function nearestIndexForView(current, view) {
    var pool = (view === 'product') ? productIndices : asciiIndices;
    if (!pool.length) return current;
    if (pool.indexOf(current) !== -1) return current;
    return pool[0];
  }

  // ── Fetch all ASCII text files ─────────────────────────────

  async function loadPortraits() {
    portraits = await Promise.all(
      characters.map(async function (char) {
        if (!char.asciiUrl) return null;
        try {
          var res = await fetch(char.asciiUrl);
          if (!res.ok) return null;
          var text = await res.text();
          var lines = text.split('\n');
          while (lines.length && !lines[lines.length - 1].trimEnd()) lines.pop();
          return lines;
        } catch (e) {
          console.warn('bmyjoy hero: failed to load ASCII for', char.name, e);
          return null;
        }
      })
    );

    // Find max grid dimensions across all valid portraits
    gridRows = 0;
    gridCols = 0;
    for (var i = 0; i < portraits.length; i++) {
      if (!portraits[i]) continue;
      gridRows = Math.max(gridRows, portraits[i].length);
      for (var j = 0; j < portraits[i].length; j++) {
        gridCols = Math.max(gridCols, portraits[i][j].length);
      }
    }

    // Pad all portraits to uniform dimensions
    for (var k = 0; k < portraits.length; k++) {
      if (!portraits[k]) {
        portraits[k] = [];
      }
      while (portraits[k].length < gridRows) {
        portraits[k].push('');
      }
      portraits[k] = portraits[k].map(function (line) {
        return line.padEnd(gridCols);
      });
    }
  }

  // ── Get a character at (row, col) for a given portrait ─────

  function getChar(portraitIndex, row, col) {
    if (portraitIndex >= portraits.length) return ' ';
    var line = portraits[portraitIndex][row];
    if (!line || col >= line.length) return ' ';
    return line[col];
  }

  // ── Random ramp character (for flip intermediates) ─────────

  function randomRampChar() {
    return RAMP[Math.floor(Math.random() * RAMP.length)];
  }

  // ── Render a portrait instantly (no animation) ─────────────

  function renderInstant(index) {
    if (!portraits[index]) return;
    asciiEl.textContent = portraits[index].join('\n');
    currentIndex = index;
    updateCTA(index);
  }

  // ── Update CTA link per character ──────────────────────────

  function updateCTA(charIndex) {
    if (!ctaEl) return;
    var char = characters[charIndex];
    if (char && char.collectionUrl) {
      ctaEl.setAttribute('href', char.collectionUrl);
    } else {
      ctaEl.setAttribute('href', ctaDefaultHref);
    }
  }

  // ── Flip animation ────────────────────────────────────────

  function flipTo(nextIndex, onComplete) {
    if (isFlipping || !portraits[nextIndex]) return;
    isFlipping = true;

    if (prefersReducedMotion.matches) {
      renderInstant(nextIndex);
      isFlipping = false;
      if (onComplete) onComplete();
      return;
    }

    var grid = [];
    for (var r = 0; r < gridRows; r++) {
      grid[r] = [];
      for (var c = 0; c < gridCols; c++) {
        grid[r][c] = getChar(currentIndex, r, c);
      }
    }

    var maxDist = gridRows + gridCols;
    var cellDelays = [];
    for (var r2 = 0; r2 < gridRows; r2++) {
      cellDelays[r2] = [];
      for (var c2 = 0; c2 < gridCols; c2++) {
        var normalizedDist = (r2 + c2) / maxDist;
        var jitter = (Math.random() - 0.5) * 0.3;
        cellDelays[r2][c2] = Math.max(0, normalizedDist + jitter) * WAVE_SPREAD;
      }
    }

    var startTime = performance.now();

    function animate(now) {
      var elapsed = now - startTime;
      var allDone = true;

      for (var r = 0; r < gridRows; r++) {
        for (var c = 0; c < gridCols; c++) {
          var cellStart = cellDelays[r][c];
          var cellElapsed = elapsed - cellStart;
          var finalChar = getChar(nextIndex, r, c);

          if (cellElapsed < 0) {
            allDone = false;
          } else if (cellElapsed >= FLIP_DURATION) {
            grid[r][c] = finalChar;
          } else {
            allDone = false;
            var progress = cellElapsed / FLIP_DURATION;
            grid[r][c] = (progress > 0.8) ? finalChar : randomRampChar();
          }
        }
      }

      var output = '';
      for (var r3 = 0; r3 < gridRows; r3++) {
        if (r3 > 0) output += '\n';
        output += grid[r3].join('');
      }
      asciiEl.textContent = output;

      if (allDone) {
        currentIndex = nextIndex;
        isFlipping = false;
        updateCTA(nextIndex);
        if (onComplete) onComplete();
      } else {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }

  // ── Initial flip-in (from random to first character) ───────

  function initialFlipIn() {
    // Find the first valid index for the active view
    var firstIdx = nearestIndexForView(0, activeView);

    if (prefersReducedMotion.matches) {
      renderInstant(firstIdx);
      startCycle();
      return;
    }

    isFlipping = true;

    var grid = [];
    for (var r = 0; r < gridRows; r++) {
      grid[r] = [];
      for (var c = 0; c < gridCols; c++) {
        grid[r][c] = randomRampChar();
      }
    }

    var maxDist = gridRows + gridCols;
    var cellDelays = [];
    for (var r2 = 0; r2 < gridRows; r2++) {
      cellDelays[r2] = [];
      for (var c2 = 0; c2 < gridCols; c2++) {
        var normalizedDist = (r2 + c2) / maxDist;
        var jitter = (Math.random() - 0.5) * 0.3;
        cellDelays[r2][c2] = Math.max(0, normalizedDist + jitter) * WAVE_SPREAD;
      }
    }

    var startTime = performance.now();

    function animate(now) {
      var elapsed = now - startTime;
      var allDone = true;

      for (var r = 0; r < gridRows; r++) {
        for (var c = 0; c < gridCols; c++) {
          var cellStart = cellDelays[r][c];
          var cellElapsed = elapsed - cellStart;
          var finalChar = getChar(firstIdx, r, c);

          if (cellElapsed < 0) {
            grid[r][c] = randomRampChar();
            allDone = false;
          } else if (cellElapsed >= FLIP_DURATION) {
            grid[r][c] = finalChar;
          } else {
            allDone = false;
            var progress = cellElapsed / FLIP_DURATION;
            grid[r][c] = (progress > 0.8) ? finalChar : randomRampChar();
          }
        }
      }

      var output = '';
      for (var r3 = 0; r3 < gridRows; r3++) {
        if (r3 > 0) output += '\n';
        output += grid[r3].join('');
      }
      asciiEl.textContent = output;

      if (allDone) {
        currentIndex = firstIdx;
        isFlipping = false;
        updateCTA(firstIdx);
        startCycle();
      } else {
        requestAnimationFrame(animate);
      }
    }

    asciiEl.textContent = '';
    requestAnimationFrame(animate);
  }

  // ── Auto-cycle ─────────────────────────────────────────────

  function startCycle() {
    stopCycle();
    if (characters.length < 2) return;
    cycleTimer = setInterval(function () {
      if (!isVisible || isFlipping) return;
      var next = nextIndexForView(currentIndex, activeView);
      if (next === currentIndex) return; // no other valid character
      if (activeView === 'ascii') {
        flipTo(next);
      } else {
        showProductImage(next);
      }
    }, cycleMs);
  }

  function stopCycle() {
    if (cycleTimer) {
      clearInterval(cycleTimer);
      cycleTimer = null;
    }
  }

  // ── Product image display ──────────────────────────────────

  function showProductImage(index) {
    // Hide all product images
    Object.keys(productImgMap).forEach(function (key) {
      productImgMap[key].classList.remove('is-active');
    });
    // Show the one for this character (if it exists)
    if (productImgMap[index]) {
      productImgMap[index].classList.add('is-active');
    }
    currentIndex = index;
    updateCTA(index);
  }

  // ── Click to cycle ─────────────────────────────────────────

  hero.addEventListener('click', function (e) {
    if (e.target.closest('[data-bmyjoy-hero-toggle]')) return;
    if (isFlipping) return;

    stopCycle();
    var next = nextIndexForView(currentIndex, activeView);
    if (next === currentIndex) return;

    if (activeView === 'ascii') {
      flipTo(next, function () { startCycle(); });
    } else {
      showProductImage(next);
      startCycle();
    }
  });

  // ── Keyboard accessibility ─────────────────────────────────

  hero.setAttribute('tabindex', '0');
  hero.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isFlipping) return;
      stopCycle();
      var next = nextIndexForView(currentIndex, activeView);
      if (next === currentIndex) return;
      if (activeView === 'ascii') {
        flipTo(next, function () { startCycle(); });
      } else {
        showProductImage(next);
        startCycle();
      }
    }
  });

  // ── Page visibility — pause when tab is hidden ─────────────

  document.addEventListener('visibilitychange', function () {
    isVisible = !document.hidden;
    if (isVisible) {
      if (!isFlipping) startCycle();
      startGlitchCycle();
    } else {
      stopCycle();
      stopGlitchCycle();
    }
  });

  // ── View toggle (ASCII / Product) ──────────────────────────

  function switchView(view) {
    if (view === activeView) return;
    activeView = view;

    if (view === 'product') {
      stopCycle();
      asciiLayer.classList.remove('is-active');
      productLayer.classList.add('is-active');
      hero.style.background =
        getComputedStyle(hero).getPropertyValue('--bmyjoy-hero-product-bg').trim() || '#101012';
      // Jump to nearest valid product character
      var prodIdx = nearestIndexForView(currentIndex, 'product');
      showProductImage(prodIdx);
      startCycle();
    } else {
      stopCycle();
      productLayer.classList.remove('is-active');
      asciiLayer.classList.add('is-active');
      hero.style.background =
        getComputedStyle(hero).getPropertyValue('--bmyjoy-hero-ascii-bg').trim() || '';
      // Jump to nearest valid ASCII character
      var ascIdx = nearestIndexForView(currentIndex, 'ascii');
      if (ascIdx !== currentIndex) {
        renderInstant(ascIdx);
      }
      startCycle();
    }
  }

  if (toggleWrap) {
    var toggleBtns = toggleWrap.querySelectorAll('[data-bmyjoy-view]');

    toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var view = btn.dataset.bmyjoyView;

        toggleBtns.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');

        switchView(view);
      });
    });
  }

  // ── Glitch engine ──────────────────────────────────────────

  var glitchTimer = null;

  function triggerGlitch() {
    if (glitchIntensity <= 0 || prefersReducedMotion.matches) return;

    // Scale shift pixels: 2px at low intensity → 10px at max
    var shiftPx = 2 + glitchIntensity * 8;
    // Scale duration: 120ms at low → 300ms at max
    var durationMs = 120 + glitchIntensity * 180;

    hero.style.setProperty('--glitch-shift', shiftPx + 'px');
    hero.style.setProperty('--glitch-duration', durationMs + 'ms');

    hero.classList.add('is-glitching');

    setTimeout(function () {
      hero.classList.remove('is-glitching');
    }, durationMs + 50);
  }

  function startGlitchCycle() {
    stopGlitchCycle();
    if (glitchIntensity <= 0) return;

    function scheduleNext() {
      // Interval: ~10s at low intensity, ~4s at high
      var minInterval = 4000;
      var maxInterval = 10000;
      var baseInterval = maxInterval - glitchIntensity * (maxInterval - minInterval);
      var jitter = (Math.random() - 0.5) * baseInterval * 0.4;
      var delay = Math.max(2000, baseInterval + jitter);

      glitchTimer = setTimeout(function () {
        if (isVisible) triggerGlitch();
        scheduleNext();
      }, delay);
    }

    // Initial delay before first glitch (1.5–3s after page load)
    glitchTimer = setTimeout(function () {
      if (isVisible) triggerGlitch();
      scheduleNext();
    }, 1500 + Math.random() * 1500);
  }

  function stopGlitchCycle() {
    if (glitchTimer) {
      clearTimeout(glitchTimer);
      glitchTimer = null;
    }
  }

  // ── Init ───────────────────────────────────────────────────

  // Set initial background based on default view
  if (defaultView === 'product') {
    hero.style.background =
      getComputedStyle(hero).getPropertyValue('--bmyjoy-hero-product-bg').trim() || '#101012';
    // Show first valid product image
    var firstProd = nearestIndexForView(0, 'product');
    showProductImage(firstProd);
  }

  loadPortraits().then(function () {
    if (gridRows > 0 && gridCols > 0) {
      if (activeView === 'ascii') {
        initialFlipIn();
      } else {
        // Product view is default — render ASCII in background for when user switches
        currentIndex = nearestIndexForView(0, 'product');
        startCycle();
      }
    }
    // Start glitch regardless of view
    startGlitchCycle();
  });
})();
